import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ClickAwayListener, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Appointment, { status as AppointmentStatusIds } from '@spike/appointment-model';
import clsx from 'clsx';
import { useApiClientWrapper, useMasterData } from 'hooks';
import { FunctionComponent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PulseLoader from 'react-spinners/PulseLoader';
import List from './List';
import {
    AppointmentsStatus,
    BookingsStatus,
    cancelAppointmentThunk,
    cancelRecurrentAppointmentsThunk,
    changeStatusAppointmentThunk,
    completeAppointmentThunk,
    confirmAppointmentThunk
} from '@spike/bookings-action';
import { RootState } from 'store';
import { OrdersStatus, reloadOrderThunk } from '@spike/orders-action';
import useNonInitialEffect from '@versiondos/hooks';
import PopUpCancelBooking, { reasonNoShowId } from 'components/UI/PopUpCancelBooking';
import { NO_SHOW_CANCEL_REASON_ID } from 'model';
import { Option } from '@spike/model';
import { CompleteAppointmentDialog } from 'components/UI';

export interface StatusButtonProps {
    appointment: Appointment;
    reloadOrderAfterStatusChange: boolean;
    disabled?: boolean;
    className?: string;
    onStartSaving?: () => void;
    onStopSaving?: () => void;
}

interface ButtonColors {
    color: string;
    backgroundColor: string;
    borderColor: string;
}

const bookedColor = { color: '#EAB464', backgroundColor: '#FFF', borderColor: '#EAB464' };
const confirmedColor = { color: '#FFF', backgroundColor: '#EAB464', borderColor: '#EAB464' };
const checkedInColor = { color: '#FFF', backgroundColor: '#BDD2CA', borderColor: '#BDD2CA' };
const inProgressColor = { color: '#FFF', backgroundColor: '#92B4A7', borderColor: '#92B4A7' };
const completedColor = { color: '#FFF', backgroundColor: '#5E8677', borderColor: '#5E8677' };
const defaultColor = { color: '#FFF', backgroundColor: '#BCB8AE', borderColor: '#BCB8AE' };

const colors = new Map<number, ButtonColors>([
    [AppointmentStatusIds.BOOKED, bookedColor],
    [AppointmentStatusIds.CONFIRMED, confirmedColor],
    [AppointmentStatusIds.CHECKED_IN, checkedInColor],
    [AppointmentStatusIds.IN_PROGRESS, inProgressColor],
    [AppointmentStatusIds.COMPLETED, completedColor],
    [AppointmentStatusIds.READY_FOR_CHECK_OUT, completedColor],
    [AppointmentStatusIds.PAYMENT, completedColor]
]);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
        },
        button: (buttonColor: ButtonColors) => ({
            display: 'flex',
            justifyContent: 'space-between',
            cursor: 'pointer',
            alignItems: 'center',
            padding: '9px 16px',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: buttonColor.borderColor,
            borderRadius: 50,
            color: buttonColor.color,
            background: buttonColor.backgroundColor
        }),
        buttonLabel: {
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '100%',
            textAlign: 'center',
            width: '100%'
        },
        chevronIcon: {
            marginLeft: 10
        },
        spinnerContainer: (buttonColor: ButtonColors) => ({
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            paddingBottom: 5,
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: buttonColor.borderColor,
            borderRadius: 50,
            color: buttonColor.color,
            background: buttonColor.backgroundColor
        })
    })
);

const getButtonLabel = (appointment: Appointment) => {
    switch (appointment.status.id) {
        case AppointmentStatusIds.COMPLETED:
        case AppointmentStatusIds.PAYMENT:
            return 'Completed';
        case AppointmentStatusIds.CANCELLED:
            return appointment.cancelationReasonId === NO_SHOW_CANCEL_REASON_ID ? 'No Show' : appointment.status.name;
        default:
            return appointment.status.name;
    }
};

export const StatusButton: FunctionComponent<StatusButtonProps> = props => {
    const color = colors.get(props.appointment.status.id) ?? defaultColor;

    const classes = useStyles(color);

    const masterData = useMasterData();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const appointmentStatus = useSelector<RootState, AppointmentsStatus>(state => state.appointments.status);
    const bookingStatus = useSelector<RootState, BookingsStatus>(state => state.bookings.status);
    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);

    const [expanded, setExpanded] = useState(false);

    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState({ cancel: false, complete: false, noShow: false });

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.ReloadSuccess:
            case OrdersStatus.Error:
                setSaving(false);
                props.onStopSaving && props.onStopSaving();
        }
    }, [ordersStatus]);

    useNonInitialEffect(() => {
        switch (appointmentStatus) {
            case AppointmentsStatus.ChangeStatusSuccess:
            case AppointmentsStatus.CompleteSuccess:
                if (props.reloadOrderAfterStatusChange) {
                    dispatch(reloadOrderThunk(apiClientWrapper));
                } else {
                    setSaving(false);
                    props.onStopSaving && props.onStopSaving();
                }
                break;
            case AppointmentsStatus.Error:
                setSaving(false);
                props.onStopSaving && props.onStopSaving();
        }
    }, [appointmentStatus]);

    useNonInitialEffect(() => {
        switch (bookingStatus) {
            case BookingsStatus.ConfirmAppointmentSuccess:
                if (props.reloadOrderAfterStatusChange) {
                    dispatch(reloadOrderThunk(apiClientWrapper));
                } else {
                    setSaving(false);
                    props.onStopSaving && props.onStopSaving();
                }
                break;
            case BookingsStatus.Error:
                setSaving(false);
                props.onStopSaving && props.onStopSaving();
        }
    }, [bookingStatus]);

    const actualStatus = useMemo(
        () => masterData.workflow.find(step => step.status.id === props.appointment.status.id),
        [props.appointment, masterData]
    );

    const actions = actualStatus?.actions.filter(action => action.show) ?? [];

    const hasActions = actions.length > 0;

    const buttonLabel = useMemo(() => getButtonLabel(props.appointment), [props.appointment]);

    const changeStatus = (statusId: number) => {
        if (statusId === AppointmentStatusIds.CONFIRMED) {
            setSaving(true);
            props.onStartSaving && props.onStartSaving();
            props.appointment.id && dispatch(confirmAppointmentThunk(apiClientWrapper, props.appointment.id));
        } else if (statusId === AppointmentStatusIds.CANCELLED) {
            setShowModal(prev => ({ ...prev, cancel: true }));
        } else if (statusId === AppointmentStatusIds.NO_SHOW) {
            setShowModal(prev => ({ ...prev, noShow: true }));
            /*
            setSaving(true);
            props.onStartSaving && props.onStartSaving();
            props.appointment.id &&
                dispatch(cancelAppointmentThunk(apiClientWrapper, props.appointment.id, false, reasonNoShowId));*/
        } else if (statusId === AppointmentStatusIds.READY_FOR_CHECK_OUT) {
            setShowModal(prev => ({ ...prev, complete: true }));
        } else {
            setSaving(true);
            props.onStartSaving && props.onStartSaving();
            props.appointment.id &&
                dispatch(changeStatusAppointmentThunk(apiClientWrapper, props.appointment.id, statusId));
        }
    };

    const clickHandler = (statusId: number) => {
        setExpanded(false);
        changeStatus(statusId);
    };

    const closeCancelationHandler = () => {
        setShowModal(prev => ({ ...prev, cancel: false, noShow: false }));
    };

    const confirmCancelHandler = (
        bookingId?: number,
        appointmentId?: number,
        sendNotification?: boolean,
        cancelationReasonId?: number
    ) => {
        if (appointmentId) {
            setSaving(true);
            props.onStartSaving && props.onStartSaving();
            closeCancelationHandler();
            dispatch(cancelAppointmentThunk(apiClientWrapper, appointmentId!, sendNotification, cancelationReasonId));
        }
    };

    const confirmCancelRecurrentHandler = (
        appointmentId?: number,
        sendNotification?: boolean,
        cancelationReasonId?: number
    ) => {
        if (appointmentId) {
            setSaving(true);
            props.onStartSaving && props.onStartSaving();
            closeCancelationHandler();
            dispatch(
                cancelRecurrentAppointmentsThunk(
                    apiClientWrapper,
                    appointmentId!,
                    AppointmentStatusIds.CANCELLED,
                    sendNotification,
                    cancelationReasonId
                )
            );
        }
    };

    const completeHandler = (appointmentId: number, sendNotification: boolean) => {
        closeCompleteModal();
        setSaving(true);
        props.onStartSaving && props.onStartSaving();
        props.appointment.id && dispatch(completeAppointmentThunk(apiClientWrapper, appointmentId, sendNotification));
    };

    const closeCompleteModal = () => {
        setShowModal(prev => ({ ...prev, complete: false }));
    };

    const spinner = (
        <Box className={classes.spinnerContainer}>
            <PulseLoader
                color={color.color}
                size={5}
            />
        </Box>
    );

    return (
        <>
            <ClickAwayListener onClickAway={() => setExpanded(false)}>
                <Box className={clsx(classes.container, props.className)}>
                    {saving && spinner}
                    <Box
                        className={classes.button}
                        onClick={() => !saving && !props.disabled && setExpanded(prev => !prev)}
                    >
                        <Typography className={classes.buttonLabel}>{buttonLabel}</Typography>
                        {hasActions && (
                            <FontAwesomeIcon
                                icon={expanded ? faChevronUp : faChevronDown}
                                className={classes.chevronIcon}
                            />
                        )}
                    </Box>
                    {expanded && hasActions && !saving && actualStatus && (
                        <List
                            actions={actions}
                            onClick={clickHandler}
                            onClose={() => setExpanded(false)}
                        />
                    )}
                </Box>
            </ClickAwayListener>
            {(showModal.cancel || showModal.noShow) && (
                <PopUpCancelBooking
                    loading={saving}
                    appointment={props.appointment}
                    initCancelationReasonId={showModal.noShow ? reasonNoShowId : undefined}
                    initSendNotification={showModal.cancel}
                    onClose={closeCancelationHandler}
                    onConfirmCancel={confirmCancelHandler}
                    onConfirmCancelRecurrent={confirmCancelRecurrentHandler}
                />
            )}
            {showModal.complete && (
                <CompleteAppointmentDialog
                    onCancel={closeCompleteModal}
                    onComplete={(sendNotification: boolean) =>
                        props.appointment.id && completeHandler(props.appointment.id, sendNotification)
                    }
                />
            )}
        </>
    );
};

export default StatusButton;
